import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { withStyles } from '@material-ui/core/styles'

import CircularProgress from '@eig-builder/core-ui/CircularProgress'
import Paper from '@eig-builder/core-ui/Paper'
import { YesNo } from '@eig-builder/module-modals/helpers'

import { NotificationBarContext } from '@eig-builder/module-notification-bar-with-context'
import { showError, showMessage, showSuccess } from '@eig-builder/module-notification-bar-with-context/actions'

import AccountDetails from './detail-card'

import PartnerEnum from '../../enums/partner-enum'
import { getAccountName } from '../../helpers/account-name-helper'

import Text from '@eig-builder/module-localization'
import './lang'

const styles = theme => ({
  content: {
    overflow: 'visible'
  },
  paper: {
    padding: theme.spacing(6),
    height: 250,
    marginBottom: theme.spacing(6)
  },
  loader: {
    marginTop: 80
  },
  accountError: {
    marginTop: 80,
    textAlign: 'center'
  }
})

class AccountDetailView extends Component {
  static contextType = NotificationBarContext

  componentDidMount () {
    if (!this.props.match.params.id || !this.props.match.params.partner) {
      return
    }

    if (this.props.account && this.props.account.id === this.props.match.params.id) {
      return
    }

    const accountId = this.props.match.params.id
    const partner = this.props.match.params.partner

    const matchPath = this.props.match.url
    const locationPath = this.props.location.pathname

    // no tab is given in the url, so default to /sites
    if (matchPath === locationPath || matchPath + '/' === locationPath) {
      this.props.history.push(`/account/${partner}/${accountId}/sites`)
    }

    this.props.actions.loadAccountById(this.getPartnerIdFromMatch(), accountId)
    this.props.actions.getGoodwillCreditsForAccount(accountId, this.getPartnerIdFromMatch())
  }

  getPartnerIdFromMatch () {
    const partner = this.props.match.params.partner.toLowerCase()

    if (isNaN(partner)) {
      const partnerObj = PartnerEnum[partner.toUpperCase()]
      return (partnerObj && partnerObj.id) || 0
    }
    return partner
  }

  handleFraudDialog = (type, account, modalDispatch) => {
    let content, yes
    switch (type) {
      case 'allow-domain-purchases': {
        content = <Text message='modules.crm.components.account.overrideDomain' />
        yes = this.props.actions.fraudPreventionAllowDomainPurchases
        break
      }
      case 'allow-mail-purchases': {
        content = <Text message='modules.crm.components.account.overrideMail' />
        yes = this.props.actions.fraudPreventionAllowMailPurchases
        break
      }
      case 'allow-change-payment-method': {
        content = <Text message='modules.crm.components.account.overridePaymentMethod' />
        yes = this.props.actions.fraudPreventionAllowChangePaymentMethod
        break
      }
    }
    this.showFraudDialog(type, account, content, yes, modalDispatch)
  }

  showFraudDialog (type, account, content, yes, modalDispatch) {
    const { partnerId, id } = account
    YesNo({
      title: <Text message='modules.crm.components.account.override' />,
      content: content,
      yes: () => {
        yes(partnerId, id)
        this.showNotification = type
      }
    })(modalDispatch)
  }

  componentDidUpdate (prevProps) {
    this.handleResponse(this.props)
    const account = this.props.account
    if (account) {
      document.title = `
        ${getAccountName(account.firstName, account.lastName)} | ${account.emailAdress}
      `
    }

    if (prevProps.restoreAccountPending && !this.props.restoreAccountPending && this.props.restoreAccountSuccess) {
      showSuccess(<Text message='modules.crm.components.account.restoreAccountSuccess' />)(this.context.dispatch)
      this.props.actions.loadAccountById(account.partnerId, account.id)
    }

    if (
      prevProps.postEmailChangeRetrieving &&
      !this.props.postEmailChangeRetrieving &&
      this.props.postEmailChangeResponse
    ) {
      showSuccess(<Text message='modules.crm.components.account.changeEmailSuccess' />)(this.context.dispatch)
      this.props.actions.loadAccountById(this.getPartnerIdFromMatch(), this.props.match.params.id)
    }

    if (
      prevProps.synchronizeCardsRetrieving &&
      !this.props.synchronizeCardsRetrieving &&
      this.props.synchronizeCardsResponse
    ) {
      showSuccess(<Text message='modules.crm.components.account.synchronizeCardsResponse' />)(this.context.dispatch)
    }
  }

  handleResponse = nextProps => {
    if (this.showNotification) {
      if (this.showNotification === 'allow-change-payment-method') {
        if (
          nextProps.allowChangePaymentMethodError &&
          nextProps.allowChangePaymentMethodError !== this.props.allowChangePaymentMethodError
        ) {
          showError(<Text message='modules.crm.components.account.allowChangePaymentMethodError' />)(
            this.context.dispatch
          )
          this.showNotification = false
        } else if (nextProps.allowChangePaymentMethodResponse === '') {
          showSuccess(<Text message='modules.crm.components.account.allowChangePaymentMethodResponse' />)(
            this.context.dispatch
          )
          this.showNotification = false
        }
      }

      if (this.showNotification === 'allow-domain-purchases') {
        if (
          nextProps.allowDomainPurchasesError &&
          nextProps.allowDomainPurchasesError !== this.props.allowDomainPurchasesError
        ) {
          showError(<Text message='modules.crm.components.account.allowDomainError' />)(this.context.dispatch)
          this.showNotification = false
        } else if (nextProps.allowDomainPurchasesResponse === '') {
          showSuccess(<Text message='modules.crm.components.account.allowDomainResponse' />)(this.context.dispatch)
          this.showNotification = false
        }
      }

      if (this.showNotification === 'allow-mail-purchases') {
        if (
          nextProps.allowMailPurchasesError &&
          nextProps.allowMailPurchasesError !== this.props.allowMailPurchasesError
        ) {
          showError(<Text message='modules.crm.components.account.allowMailError' />)(this.context.dispatch)
          this.showNotification = false
        } else if (nextProps.allowMailPurchasesResponse === '') {
          showSuccess(<Text message='modules.crm.components.account.allowMailResponse' />)(this.context.dispatch)
          this.showNotification = false
        }
      }
    }
  }

  restoreAccount = ({ id, partnerId }) => {
    this.showNotification = true
    this.props.actions.restoreAccountById(id, partnerId)
  }

  synchronizeCards = ({ id, partnerId }) => {
    showMessage(<Text message='modules.crm.components.account.synchronizeCardsPending' />)(this.context.dispatch)
    this.props.actions.synchronizeCards(id, partnerId)
  }

  render () {
    const { account, classes, accountRetrieving, accountError } = this.props

    if ((accountRetrieving && !account) || accountError || !account) {
      return (
        <Paper className={classes.paper}>
          {accountRetrieving && <CircularProgress className={classes.loader} centered />}
          {accountError && (
            <div className={classes.accountError}>
              <div>
                {' '}
                <Text message='modules.crm.components.account.loadAccountError' />
              </div>
              <div>{typeof accountError === 'object' ? accountError.message : accountError}</div>
            </div>
          )}
        </Paper>
      )
    }

    return (
      <AccountDetails
        account={account}
        handleFraudDialog={this.handleFraudDialog}
        synchronizeCards={this.synchronizeCards}
        restoreAccount={this.restoreAccount}
        goodwillCredits={this.props.goodwillCredits}
      />
    )
  }
}

AccountDetailView.propTypes = {
  actions: PropTypes.object.isRequired,
  account: PropTypes.object,
  accountRetrieving: PropTypes.bool,
  accountError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  postEmailChangeRetrieving: PropTypes.bool,
  postEmailChangeResponse: PropTypes.string,
  allowChangePaymentMethodError: PropTypes.object,
  allowDomainPurchasesError: PropTypes.object,
  allowMailPurchasesError: PropTypes.object,
  match: PropTypes.object.isRequired,
  synchronizeCardsRetrieving: PropTypes.bool,
  synchronizeCardsResponse: PropTypes.string,
  classes: PropTypes.object.isRequired,
  goodwillCredits: PropTypes.number,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  restoreAccountPending: PropTypes.bool,
  restoreAccountSuccess: PropTypes.bool
}

export default withStyles(styles)(withRouter(AccountDetailView))
